import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
  submitStudentSolution, 
  downloadOriginalAssignment, 
  downloadMySubmission, 
  downloadFeedbackFile,
  setUploadingHomeworkId 
} from "../../paper-assignment/paperAssignmentSlice";

const PaperAssignmentModal = ({ isOpen, onClose, assignment }) => {
  const dispatch = useDispatch();
  const { student } = useSelector((state) => state.student);
  const { uploadingHomeworkId } = useSelector((state) => state.paperAssignment);
  
  if (!isOpen || !assignment || assignment.type !== 'paper_assignment') {
    return null;
  }

  const paperData = assignment.paper_assignment_data;
  const homework = paperData.homework;
  const isCompleted = assignment.is_finished;

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    // 파일 개수 제한: 최대 20개
    if (files.length > 20) {
      alert('최대 20개의 파일만 업로드할 수 있습니다.');
      return;
    }

    // 허용된 파일 타입: PDF, 이미지, 비디오
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/quicktime',
      'video/x-msvideo',
      'video/webm'
    ];

    // 각 파일 검증
    const maxSize = 300 * 1024 * 1024; // 300MB in bytes
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!allowedTypes.includes(file.type)) {
        alert(`파일 "${file.name}": PDF, 이미지(JPG, PNG, GIF, WebP) 또는 비디오(MP4, MOV, AVI, WebM) 파일만 업로드 가능합니다.`);
        return;
      }

      // 파일 크기 제한: 300MB per file
      if (file.size > maxSize) {
        alert(`파일 "${file.name}": 파일 크기는 300MB를 초과할 수 없습니다.`);
        return;
      }
    }

    if (!student || !student.id) {
      alert('학생 정보를 찾을 수 없습니다. 페이지를 새로고침해주세요.');
      return;
    }

    dispatch(setUploadingHomeworkId(homework.id));

    try {
      const formData = new FormData();
      formData.append('homework_id', homework.id);

      // 여러 파일을 FormData에 추가
      for (let i = 0; i < files.length; i++) {
        formData.append('solution_files', files[i]);
      }

      const result = await dispatch(submitStudentSolution({
        studentId: student.id,
        formData
      }));

      if (submitStudentSolution.fulfilled.match(result)) {
        const fileCount = files.length;
        alert(`${fileCount}개의 과제 풀이 파일이 성공적으로 업로드되었습니다!`);
        onClose(); // 모달 닫기
        // 과제 목록 새로고침은 부모 컴포넌트에서 처리
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('업로드 중 오류가 발생했습니다.');
    } finally {
      dispatch(setUploadingHomeworkId(null));
    }
  };

  const handleDownload = async (type, s3Key, fileName) => {
    try {
      let result;
      console.log(`Attempting to download ${type} file:`, { s3Key, fileName });
      
      switch (type) {
        case 'original':
          result = await dispatch(downloadOriginalAssignment({ studentId: student.id, s3Key }));
          console.log('Original download result:', result);
          if (downloadOriginalAssignment.fulfilled.match(result)) {
            window.open(result.payload.download_url, '_blank');
          } else {
            alert('원본 과제 다운로드에 실패했습니다.');
          }
          break;
        case 'submission':
          result = await dispatch(downloadMySubmission({ studentId: student.id, s3Key }));
          console.log('Submission download result:', result);
          if (downloadMySubmission.fulfilled.match(result)) {
            window.open(result.payload.download_url, '_blank');
          } else {
            alert('제출물 다운로드에 실패했습니다.');
          }
          break;
        case 'feedback':
          result = await dispatch(downloadFeedbackFile({ studentId: student.id, s3Key }));
          console.log('Feedback download result:', result);
          if (downloadFeedbackFile.fulfilled.match(result)) {
            window.open(result.payload.download_url, '_blank');
          } else {
            alert('첨삭 파일 다운로드에 실패했습니다.');
          }
          break;
      }
    } catch (error) {
      console.error('Download error:', error);
      alert(`다운로드 중 오류가 발생했습니다: ${error.message}`);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '24px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid #EBEBEB'
        }}>
          <div>
            <h3 style={{ margin: 0, color: '#11346A', fontSize: '18px', fontWeight: 'bold' }}>
              {assignment.title}
            </h3>
            <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
              {isCompleted ? '학습내역 보기' : '학습하기'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div>
          {/* 수업 내용 */}
          {(() => {
            let classContent = paperData.class_content;
            
            // class_content가 문자열인 경우 JSON 파싱
            if (typeof classContent === 'string') {
              try {
                classContent = JSON.parse(classContent);
              } catch (error) {
                console.warn('Failed to parse class_content in modal:', error);
                classContent = [];
              }
            }
            
            return classContent && Array.isArray(classContent) && classContent.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#11346A' }}>📚 수업 내용</h4>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #dee2e6'
                }}>
                  {classContent.map((content, index) => {
                  const typeEmoji = content.type === 'lesson' ? '📖' : 
                                   content.type === 'exam' ? '📋' : '📝';
                  const typeName = content.type === 'lesson' ? '수업' : 
                                  content.type === 'exam' ? '시험' : '과제';
                  
                  return (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      padding: '6px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}>
                      <span>{typeEmoji}</span>
                      <span style={{ fontWeight: 'bold', color: '#495057' }}>{typeName}:</span>
                      <span style={{ flex: 1 }}>{content.title}</span>
                      {(content.score !== null && content.score !== undefined) && (content.type === 'exam' || content.type === 'homework') && (
                        <span style={{
                          color: '#007bff',
                          fontWeight: 'bold',
                          backgroundColor: '#e3f2fd',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '12px'
                        }}>
                          {content.score}/{content.totalScore || 100}점
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })()}

          {/* 과제 정보 */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <h4 style={{ margin: 0, fontSize: '16px' }}>📄 {homework.title}</h4>
              {homework.s3_key && homework.file_name && (
                <button
                  onClick={() => handleDownload('original', homework.s3_key, homework.file_name)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  과제 다운로드
                </button>
              )}
            </div>
            {homework.file_name && (
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                파일: {homework.file_name}
              </p>
            )}
          </div>

          {!isCompleted ? (
            /* 미완료 - 제출 폼 */
            <div>
              <h5 style={{ marginBottom: '12px' }}>과제 제출</h5>
              {paperData.student_submission ? (
                <div style={{ padding: '12px', backgroundColor: '#d4edda', borderRadius: '6px' }}>
                  <p style={{ margin: '0 0 8px 0', color: '#155724', fontWeight: 'bold' }}>
                    ✓ 이미 제출된 과제입니다
                  </p>
                  {paperData.student_submission.submission_files && paperData.student_submission.submission_files.length > 0 ? (
                    <div>
                      <p style={{ margin: '8px 0 4px 0', color: '#155724', fontSize: '13px' }}>
                        제출된 파일 ({paperData.student_submission.submission_files.length}개):
                      </p>
                      <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px', color: '#155724', fontSize: '13px' }}>
                        {paperData.student_submission.submission_files.map((file, index) => (
                          <li key={index}>{file.name}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p style={{ margin: '4px 0 0 0', color: '#155724', fontSize: '13px' }}>
                      {paperData.student_submission.submission_file_name}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.webm,image/*,video/*,application/pdf"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) {
                        handleFileUpload(files);
                      }
                    }}
                    disabled={uploadingHomeworkId === homework.id}
                    style={{ marginBottom: '8px' }}
                  />
                  {uploadingHomeworkId === homework.id && (
                    <p style={{ margin: 0, color: '#007bff', fontSize: '14px' }}>
                      업로드 중...
                    </p>
                  )}
                  <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                    PDF, 이미지, 비디오 파일을 업로드할 수 있습니다. (최대 20개, 각 파일 300MB 이하)
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* 완료 - 결과 표시 */
            <div>
              <div style={{ marginBottom: '16px' }}>
                <h5 style={{ marginBottom: '8px' }}>학습 결과</h5>
                {(() => {
                  const score = assignment.score;
                  return score !== null && score !== undefined && (
                    <div style={{
                      padding: '8px 12px',
                      backgroundColor: score >= 80 ? '#d4edda' : 
                                     score >= 60 ? '#fff3cd' : '#f8d7da',
                      color: score >= 80 ? '#155724' : 
                            score >= 60 ? '#856404' : '#721c24',
                      borderRadius: '6px',
                      fontWeight: 'bold',
                      marginBottom: '12px'
                    }}>
                      점수: {score}점
                    </div>
                  );
                })()}
                
                {/* 제출 시간 표시 */}
                {paperData.student_submission?.submitted_at && (
                  <div style={{
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    color: '#495057',
                    borderRadius: '6px',
                    marginBottom: '12px',
                    fontSize: '14px'
                  }}>
                    제출 시간: {new Date(paperData.student_submission.submitted_at).toLocaleString('ko-KR')}
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  {paperData.student_submission && (
                    <button
                      onClick={() => handleDownload('submission', paperData.student_submission.submission_s3_key, paperData.student_submission.submission_file_name)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '13px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      내 풀이 보기
                    </button>
                  )}
                  
                  {paperData.student_submission?.feedback_file_name && (
                    <button
                      onClick={() => handleDownload('feedback', paperData.student_submission.feedback_s3_key, paperData.student_submission.feedback_file_name)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '13px',
                        backgroundColor: '#fd7e14',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      첨삭 파일 보기
                    </button>
                  )}
                </div>

                <div style={{ fontSize: '13px', color: '#666' }}>
                  {paperData.student_submission && (
                    <div>
                      {paperData.student_submission.submission_files && paperData.student_submission.submission_files.length > 0 ? (
                        <div>
                          <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>제출 파일 ({paperData.student_submission.submission_files.length}개):</p>
                          <ul style={{ margin: '4px 0 8px 0', paddingLeft: '20px' }}>
                            {paperData.student_submission.submission_files.map((file, index) => (
                              <li key={index}>{file.name}</li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <p>제출 파일: {paperData.student_submission.submission_file_name}</p>
                      )}
                    </div>
                  )}
                  {paperData.student_submission?.feedback_file_name && (
                    <p>첨삭 파일: {paperData.student_submission.feedback_file_name}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaperAssignmentModal;